import { useState, useEffect } from 'react';

interface ApprovalSettingsProps {
  formId: string;
  isOpen: boolean;
  onClose: () => void;
}

interface Approver {
  collaboratorId: string;
  email: string;
  displayName: string | null;
  status: 'pending' | 'approved' | 'rejected';
  comment: string | null;
  approvedAt: string | null;
  lastReminderSent: string | null;
  remindersSent: number;
}

interface ApprovalData {
  requiresApproval: boolean;
  overallStatus: 'not_required' | 'pending' | 'approved' | 'rejected';
  settings: {
    approvalType: 'any' | 'all';
    notifyOnApprovalNeeded: boolean;
    notifyOnApproved: boolean;
    notifyOnRejected: boolean;
    notifyCreatorOnEach: boolean;
    reminderEnabled: boolean;
    reminderIntervalHours: number;
    maxReminders: number;
    approvalRequestMessage: string | null;
    approvalDeadline: string | null;
  };
  approvers: Approver[];
  summary: {
    total: number;
    pending: number;
    approved: number;
    rejected: number;
  };
}

export default function ApprovalSettings({ formId, isOpen, onClose }: ApprovalSettingsProps) {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [data, setData] = useState<ApprovalData | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<'settings' | 'approvers' | 'history'>('settings');

  // Settings form state
  const [approvalType, setApprovalType] = useState<'any' | 'all'>('all');
  const [notifyOnApprovalNeeded, setNotifyOnApprovalNeeded] = useState(true);
  const [notifyOnApproved, setNotifyOnApproved] = useState(true);
  const [notifyOnRejected, setNotifyOnRejected] = useState(true);
  const [notifyCreatorOnEach, setNotifyCreatorOnEach] = useState(false);
  const [reminderEnabled, setReminderEnabled] = useState(true);
  const [reminderIntervalHours, setReminderIntervalHours] = useState(24);
  const [maxReminders, setMaxReminders] = useState(3);
  const [approvalRequestMessage, setApprovalRequestMessage] = useState('');

  useEffect(() => {
    if (isOpen && formId) {
      loadApprovalData();
    }
  }, [isOpen, formId]);

  async function loadApprovalData() {
    try {
      setLoading(true);
      setError(null);
      const response = await fetch(`/api/forms/${formId}/approval`, {
        credentials: 'include',
      });

      if (!response.ok) {
        throw new Error('Failed to load approval settings');
      }

      const result = await response.json() as ApprovalData;
      setData(result);

      // Update form state
      setApprovalType(result.settings.approvalType);
      setNotifyOnApprovalNeeded(result.settings.notifyOnApprovalNeeded);
      setNotifyOnApproved(result.settings.notifyOnApproved);
      setNotifyOnRejected(result.settings.notifyOnRejected);
      setNotifyCreatorOnEach(result.settings.notifyCreatorOnEach);
      setReminderEnabled(result.settings.reminderEnabled);
      setReminderIntervalHours(result.settings.reminderIntervalHours);
      setMaxReminders(result.settings.maxReminders);
      setApprovalRequestMessage(result.settings.approvalRequestMessage || '');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load');
    } finally {
      setLoading(false);
    }
  }

  async function saveSettings() {
    try {
      setSaving(true);
      setError(null);

      const response = await fetch(`/api/forms/${formId}/approval?action=update-settings`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          approvalType,
          notifyOnApprovalNeeded,
          notifyOnApproved,
          notifyOnRejected,
          notifyCreatorOnEach,
          reminderEnabled,
          reminderIntervalHours,
          maxReminders,
          approvalRequestMessage: approvalRequestMessage || null,
        }),
      });

      if (!response.ok) {
        throw new Error('Failed to save settings');
      }

      await loadApprovalData();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save');
    } finally {
      setSaving(false);
    }
  }

  async function requestApproval() {
    try {
      setSaving(true);
      setError(null);

      const response = await fetch(`/api/forms/${formId}/approval?action=request`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          message: approvalRequestMessage || null,
        }),
      });

      if (!response.ok) {
        const result = await response.json() as { error?: string };
        throw new Error(result.error || 'Failed to request approval');
      }

      await loadApprovalData();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to request approval');
    } finally {
      setSaving(false);
    }
  }

  async function sendReminder(approverId?: string) {
    try {
      setSaving(true);
      setError(null);

      const response = await fetch(`/api/forms/${formId}/approval?action=remind`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          approverId,
        }),
      });

      if (!response.ok) {
        const result = await response.json() as { error?: string };
        throw new Error(result.error || 'Failed to send reminder');
      }

      const result = await response.json() as { sentCount: number };
      alert(`Sent ${result.sentCount} reminder(s)`);
      await loadApprovalData();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to send reminder');
    } finally {
      setSaving(false);
    }
  }

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-purple-100 flex items-center justify-center">
              <svg className="w-5 h-5 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
            <div>
              <h2 className="text-lg font-semibold text-gray-900">Approval Settings</h2>
              <p className="text-sm text-gray-500">Configure form approval workflow</p>
            </div>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-lg transition-colors">
            <svg className="w-5 h-5 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Status Banner */}
        {data && data.requiresApproval && (
          <div className={`px-6 py-3 ${
            data.overallStatus === 'approved' ? 'bg-green-50 border-b border-green-100' :
            data.overallStatus === 'rejected' ? 'bg-red-50 border-b border-red-100' :
            data.overallStatus === 'pending' ? 'bg-amber-50 border-b border-amber-100' :
            'bg-gray-50 border-b border-gray-100'
          }`}>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                  data.overallStatus === 'approved' ? 'bg-green-100 text-green-800' :
                  data.overallStatus === 'rejected' ? 'bg-red-100 text-red-800' :
                  data.overallStatus === 'pending' ? 'bg-amber-100 text-amber-800' :
                  'bg-gray-100 text-gray-800'
                }`}>
                  {data.overallStatus === 'approved' ? 'Approved' :
                   data.overallStatus === 'rejected' ? 'Rejected' :
                   data.overallStatus === 'pending' ? 'Pending Approval' :
                   'Not Required'}
                </span>
                <span className="text-sm text-gray-600">
                  {data.summary.approved}/{data.summary.total} approved
                  {approvalType === 'any' && data.summary.total > 0 && ' (only 1 needed)'}
                </span>
              </div>
              {data.overallStatus === 'pending' && data.summary.pending > 0 && (
                <button
                  onClick={() => sendReminder()}
                  disabled={saving}
                  className="text-sm text-amber-700 hover:text-amber-800 font-medium"
                >
                  Send Reminders
                </button>
              )}
            </div>
          </div>
        )}

        {/* Tabs */}
        <div className="flex border-b border-gray-200">
          {(['settings', 'approvers'] as const).map((tab) => (
            <button
              key={tab}
              onClick={() => setActiveTab(tab)}
              className={`flex-1 px-4 py-3 text-sm font-medium transition-colors ${
                activeTab === tab
                  ? 'text-purple-600 border-b-2 border-purple-600'
                  : 'text-gray-500 hover:text-gray-700'
              }`}
            >
              {tab === 'settings' ? 'Settings' : 'Approvers'}
            </button>
          ))}
        </div>

        {/* Content */}
        <div className="p-6 overflow-y-auto max-h-[calc(90vh-250px)]">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-600"></div>
            </div>
          ) : error ? (
            <div className="text-center py-8">
              <p className="text-red-600 mb-4">{error}</p>
              <button onClick={loadApprovalData} className="btn-secondary">
                Retry
              </button>
            </div>
          ) : (
            <>
              {activeTab === 'settings' && (
                <div className="space-y-6">
                  {/* Approval Type */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-3">
                      Approval Process
                    </label>
                    <div className="grid grid-cols-2 gap-3">
                      <button
                        type="button"
                        onClick={() => setApprovalType('all')}
                        className={`p-4 rounded-lg border-2 text-left transition-colors ${
                          approvalType === 'all'
                            ? 'border-purple-500 bg-purple-50'
                            : 'border-gray-200 hover:border-gray-300'
                        }`}
                      >
                        <div className="flex items-center gap-2 mb-1">
                          <svg className="w-5 h-5 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                          </svg>
                          <span className="font-medium text-gray-900">Approved by All</span>
                        </div>
                        <p className="text-sm text-gray-500">
                          All approvers must approve for the form to be approved
                        </p>
                      </button>
                      <button
                        type="button"
                        onClick={() => setApprovalType('any')}
                        className={`p-4 rounded-lg border-2 text-left transition-colors ${
                          approvalType === 'any'
                            ? 'border-purple-500 bg-purple-50'
                            : 'border-gray-200 hover:border-gray-300'
                        }`}
                      >
                        <div className="flex items-center gap-2 mb-1">
                          <svg className="w-5 h-5 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                          </svg>
                          <span className="font-medium text-gray-900">Approved by Any</span>
                        </div>
                        <p className="text-sm text-gray-500">
                          Only one approver needs to approve for the form to be approved
                        </p>
                      </button>
                    </div>
                  </div>

                  {/* Email Notifications */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-3">
                      Email Notifications
                    </label>
                    <div className="space-y-3">
                      <label className="flex items-center gap-3">
                        <input
                          type="checkbox"
                          checked={notifyOnApprovalNeeded}
                          onChange={(e) => setNotifyOnApprovalNeeded(e.target.checked)}
                          className="w-4 h-4 text-purple-600 rounded border-gray-300 focus:ring-purple-500"
                        />
                        <span className="text-sm text-gray-700">
                          Notify approvers when approval is requested
                        </span>
                      </label>
                      <label className="flex items-center gap-3">
                        <input
                          type="checkbox"
                          checked={notifyOnApproved}
                          onChange={(e) => setNotifyOnApproved(e.target.checked)}
                          className="w-4 h-4 text-purple-600 rounded border-gray-300 focus:ring-purple-500"
                        />
                        <span className="text-sm text-gray-700">
                          Notify me when form is approved
                        </span>
                      </label>
                      <label className="flex items-center gap-3">
                        <input
                          type="checkbox"
                          checked={notifyOnRejected}
                          onChange={(e) => setNotifyOnRejected(e.target.checked)}
                          className="w-4 h-4 text-purple-600 rounded border-gray-300 focus:ring-purple-500"
                        />
                        <span className="text-sm text-gray-700">
                          Notify me when form is rejected
                        </span>
                      </label>
                      <label className="flex items-center gap-3">
                        <input
                          type="checkbox"
                          checked={notifyCreatorOnEach}
                          onChange={(e) => setNotifyCreatorOnEach(e.target.checked)}
                          className="w-4 h-4 text-purple-600 rounded border-gray-300 focus:ring-purple-500"
                        />
                        <span className="text-sm text-gray-700">
                          Notify me on each individual approval/rejection
                        </span>
                      </label>
                    </div>
                  </div>

                  {/* Reminder Settings */}
                  <div>
                    <label className="flex items-center gap-3 mb-3">
                      <input
                        type="checkbox"
                        checked={reminderEnabled}
                        onChange={(e) => setReminderEnabled(e.target.checked)}
                        className="w-4 h-4 text-purple-600 rounded border-gray-300 focus:ring-purple-500"
                      />
                      <span className="text-sm font-medium text-gray-700">
                        Enable automatic reminders
                      </span>
                    </label>
                    {reminderEnabled && (
                      <div className="ml-7 grid grid-cols-2 gap-4">
                        <div>
                          <label className="block text-sm text-gray-600 mb-1">
                            Reminder interval
                          </label>
                          <select
                            value={reminderIntervalHours}
                            onChange={(e) => setReminderIntervalHours(Number(e.target.value))}
                            className="input"
                          >
                            <option value={12}>Every 12 hours</option>
                            <option value={24}>Every 24 hours</option>
                            <option value={48}>Every 2 days</option>
                            <option value={72}>Every 3 days</option>
                            <option value={168}>Every week</option>
                          </select>
                        </div>
                        <div>
                          <label className="block text-sm text-gray-600 mb-1">
                            Maximum reminders
                          </label>
                          <select
                            value={maxReminders}
                            onChange={(e) => setMaxReminders(Number(e.target.value))}
                            className="input"
                          >
                            <option value={1}>1 reminder</option>
                            <option value={2}>2 reminders</option>
                            <option value={3}>3 reminders</option>
                            <option value={5}>5 reminders</option>
                            <option value={10}>10 reminders</option>
                          </select>
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Custom Message */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Custom message for approvers (optional)
                    </label>
                    <textarea
                      value={approvalRequestMessage}
                      onChange={(e) => setApprovalRequestMessage(e.target.value)}
                      placeholder="Add a message that will be included in approval request emails..."
                      rows={3}
                      className="input"
                    />
                  </div>
                </div>
              )}

              {activeTab === 'approvers' && (
                <div className="space-y-4">
                  {data?.approvers && data.approvers.length > 0 ? (
                    <div className="divide-y divide-gray-100">
                      {data.approvers.map((approver) => (
                        <div key={approver.collaboratorId} className="py-4 first:pt-0 last:pb-0">
                          <div className="flex items-center justify-between">
                            <div className="flex items-center gap-3">
                              <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                                approver.status === 'approved' ? 'bg-green-100' :
                                approver.status === 'rejected' ? 'bg-red-100' :
                                'bg-gray-100'
                              }`}>
                                {approver.status === 'approved' ? (
                                  <svg className="w-5 h-5 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                                  </svg>
                                ) : approver.status === 'rejected' ? (
                                  <svg className="w-5 h-5 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                  </svg>
                                ) : (
                                  <svg className="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                                  </svg>
                                )}
                              </div>
                              <div>
                                <p className="font-medium text-gray-900">
                                  {approver.displayName || approver.email}
                                </p>
                                {approver.displayName && (
                                  <p className="text-sm text-gray-500">{approver.email}</p>
                                )}
                                {approver.comment && (
                                  <p className="text-sm text-gray-600 italic mt-1">
                                    "{approver.comment}"
                                  </p>
                                )}
                              </div>
                            </div>
                            <div className="flex items-center gap-2">
                              <span className={`text-xs px-2 py-1 rounded-full ${
                                approver.status === 'approved' ? 'bg-green-100 text-green-700' :
                                approver.status === 'rejected' ? 'bg-red-100 text-red-700' :
                                'bg-amber-100 text-amber-700'
                              }`}>
                                {approver.status === 'approved' ? 'Approved' :
                                 approver.status === 'rejected' ? 'Rejected' :
                                 'Pending'}
                              </span>
                              {approver.status === 'pending' && (
                                <button
                                  onClick={() => sendReminder(approver.collaboratorId)}
                                  disabled={saving || approver.remindersSent >= maxReminders}
                                  className="text-xs text-purple-600 hover:text-purple-700 font-medium disabled:opacity-50"
                                  title={approver.remindersSent >= maxReminders ? 'Max reminders reached' : 'Send reminder'}
                                >
                                  Remind ({approver.remindersSent}/{maxReminders})
                                </button>
                              )}
                            </div>
                          </div>
                          {approver.approvedAt && (
                            <p className="text-xs text-gray-500 mt-2 ml-13">
                              {approver.status === 'approved' ? 'Approved' : 'Rejected'} on{' '}
                              {new Date(approver.approvedAt).toLocaleDateString()}
                            </p>
                          )}
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="text-center py-8">
                      <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
                        <svg className="w-8 h-8 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                        </svg>
                      </div>
                      <h3 className="text-lg font-medium text-gray-900 mb-2">No approvers assigned</h3>
                      <p className="text-sm text-gray-500 mb-4">
                        Add collaborators with approval permission to enable the approval workflow.
                      </p>
                    </div>
                  )}
                </div>
              )}
            </>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between px-6 py-4 border-t border-gray-200 bg-gray-50">
          <button onClick={onClose} className="btn-secondary">
            Cancel
          </button>
          <div className="flex items-center gap-3">
            {activeTab === 'settings' && (
              <button
                onClick={saveSettings}
                disabled={saving}
                className="btn-secondary"
              >
                {saving ? 'Saving...' : 'Save Settings'}
              </button>
            )}
            {data?.requiresApproval && data.approvers.length > 0 && (
              <button
                onClick={requestApproval}
                disabled={saving}
                className="btn-primary"
              >
                {saving ? 'Sending...' : 'Request Approval'}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
